import { useMemo } from "react";

import { useQuery } from "@tanstack/react-query";
import {
  compact,
  filter,
  find,
  first,
  flatMap,
  groupBy,
  keyBy,
  map,
  some,
  uniqBy,
} from "lodash/fp";

import { isPaidPlan } from "@app/analytics/account/Billing/utils";
import useBillingContext from "@app/analytics/hooks/billing/useBillingContext";
import type { FeatureAccess } from "@app/analytics/providers/FeatureAccessContext";
import apiClient from "@app/common/api/apiClient";
import useAuth from "@app/common/auth/useAuth";
import {
  CALENDAR_PROVIDERS_BY_ID,
  INTEGRATION_PROVIDERS,
  INTEGRATION_PROVIDERS_BY_ID,
  WORKSPACE_INTEGRATION_PROVIDERS,
} from "@app/common/constants/providers";
import useFeatureAccess from "@app/common/hooks/useFeatureAccess";
import type {
  ApiIntegration,
  BillingPlan,
  CalendarProviderId,
  Integration,
  IntegrationAlert,
  IntegrationProvider,
  IntegrationProviderCategory,
  IntegrationProviderId,
  IntegrationScope,
  IntegrationSetting,
  IntegrationSettingsKind,
  User,
  UserIntegrationSetting,
} from "@app/common/types";

const FIVE_MINUTES = 5 * 60 * 1000;

type GetUrlFn = (opts: {
  providerId: IntegrationProviderId;
  scope?: IntegrationScope;
}) => string | null;

export const getIntegrationSettingsQueryKey = (
  userId?: User["id"],
  workspaceId?: User["workspaceId"]
) => compact(["integrations", "settings", userId, workspaceId]);

const isIntegrationDisabled = (
  provider: IntegrationProvider,
  billingPlan: BillingPlan,
  accessCheck: FeatureAccess["canAccessIntegration"]
) => {
  if (provider.isPremium && !isPaidPlan(billingPlan)) {
    return true;
  }

  return !accessCheck(provider.id);
};

/** Integration settings for the same parent provider share some values
 * (e.g., management urls), so if there is more than one setting, we can pull
 * some values off the first one and assume it applies to the provider as a
 * whole */
const getDefaultIntegrationSetting = (
  integrationSettings?: IntegrationSetting[]
) => first(integrationSettings);

const isUserIntegrationSetting = (
  setting: IntegrationSetting
): setting is UserIntegrationSetting => "userTokenId" in setting;

export const getAlerts = (
  settingsOrIntegration?: IntegrationSetting[] | ApiIntegration
): IntegrationAlert[] => {
  if (!settingsOrIntegration) return [];

  if (Array.isArray(settingsOrIntegration)) {
    return uniqBy("kind", flatMap("alerts", settingsOrIntegration));
  }
  return settingsOrIntegration.alerts;
};

export const getReauthRequired = (alerts: IntegrationAlert[]) =>
  some((a) => a.kind.includes("reauth_required"), alerts);

export const getLimitedData = (alerts: IntegrationAlert[]) =>
  some(
    (alert) =>
      alert.kind === "microsoft_admin_consent_granted_reauth_required" ||
      alert.kind === "microsoft_admin_consent_required",
    alerts
  );

export const getProviderEmail = (settings: IntegrationSetting[]) =>
  compact(
    map(
      (s) =>
        s.enabled && isUserIntegrationSetting(s) ? s.providerEmail : null,
      settings
    )
  )[0];

const useIntegrations = (
  { enabled = true }: { enabled?: boolean } = { enabled: true }
) => {
  const { user } = useAuth();
  const { currentPlan } = useBillingContext();
  const featureAccess = useFeatureAccess();

  const {
    data: userIntegrationSettingsResponse,
    isLoading: isUserIntegrationSettingsLoading,
  } = useQuery({
    enabled,
    queryKey: getIntegrationSettingsQueryKey(user?.id),
    queryFn: apiClient.integrationSettings.listUserIntegrationSettings,
    staleTime: FIVE_MINUTES,
  });

  const {
    data: workspaceIntegrationSettingsResponse,
    isLoading: isWorkspaceIntegrationsLoading,
  } = useQuery({
    enabled: enabled && Boolean(user?.workspaceId),
    queryKey: getIntegrationSettingsQueryKey(user?.id, user?.workspaceId),
    queryFn: () =>
      apiClient.integrationSettings.listWorkspaceIntegrationSettings(
        user?.workspaceId
      ),
    staleTime: FIVE_MINUTES,
  });

  const isLoading =
    isUserIntegrationSettingsLoading || isWorkspaceIntegrationsLoading;

  const {
    integrations: userIntegrations,
    integrationsByCategory: userIntegrationsByCategory,
  } = useMemo(() => {
    const integrations: Integration[] = compact(
      INTEGRATION_PROVIDERS.map((provider) => {
        if (provider.disabled?.()) return null;

        const isDisabled = isIntegrationDisabled(
          provider,
          currentPlan.type,
          featureAccess.canAccessIntegration
        );

        // find integration settings for the provider
        const integrationSettingsForProvider = filter(
          (setting) =>
            provider.integrationSettingsKinds.includes(setting.integrationKind),
          userIntegrationSettingsResponse
        );

        // integration is connected if the userTokenId is not null; if any of
        // the settings are connected, we consider the parent integration to be
        // connected
        const isConnected = some("userTokenId", integrationSettingsForProvider);

        const alerts = getAlerts(integrationSettingsForProvider);

        return {
          providerId: provider.id,
          kind: provider.kind,
          alerts,
          category: provider.category,
          providerEmail: getProviderEmail(integrationSettingsForProvider),
          displayName: provider.displayName,
          displayNameShort: provider.displayNameShort,
          documentationUrl: provider.documentationUrl,
          icon: provider.icon,
          integrationSettings: {
            byId: keyBy("integrationKind", integrationSettingsForProvider),
            all: integrationSettingsForProvider,
          },
          isConnected,
          isDisabled,
          isPremium: provider.isPremium,
          limitedData: getLimitedData(alerts),
          reauthRequired: getReauthRequired(alerts),
          route: provider.integrationUrl,
          scope: "user",
        };
      })
    );

    const integrationsByCategory = groupBy("category", integrations) as {
      [key in IntegrationProviderCategory]: Integration[];
    };

    return { integrations, integrationsByCategory };
  }, [
    userIntegrationSettingsResponse,
    currentPlan.type,
    user?.accessPermissions,
  ]);

  const {
    integrations: workspaceIntegrations,
    integrationsById: workspaceIntegrationsById,
  } = useMemo(() => {
    const integrations: Integration[] = compact(
      WORKSPACE_INTEGRATION_PROVIDERS.map((provider) => {
        const isDisabled = isIntegrationDisabled(
          provider,
          currentPlan.type,
          featureAccess.canAccessIntegration
        );

        // find integration settings for the provider
        const integrationSettingsForProvider = filter(
          (setting) =>
            provider.integrationSettingsKinds.includes(setting.integrationKind),
          workspaceIntegrationSettingsResponse
        );

        // integration is connected if the workspace token id is not null; if
        // any of the settings are connected, we consider the parent integration
        // to be connected
        const isConnected = some(
          "workspaceTokenId",
          integrationSettingsForProvider
        );

        const alerts = getAlerts(integrationSettingsForProvider);

        return {
          providerId: provider.id,
          kind: provider.kind,
          alerts,
          category: provider.category,
          displayName: provider.displayName,
          displayNameShort: provider.displayNameShort,
          documentationUrl: provider.workspaceDocumentationUrl,
          icon: provider.icon,
          integrationSettings: {
            byId: keyBy("integrationKind", integrationSettingsForProvider),
            all: integrationSettingsForProvider,
          },
          isConnected,
          isDisabled,
          isPremium: provider.isPremium,
          limitedData: getLimitedData(alerts),
          reauthRequired: getReauthRequired(alerts),
          route: provider.workspaceIntegrationUrl,
          scope: "workspace",
        };
      })
    );
    const integrationsById = keyBy("providerId", integrations) as {
      [key in IntegrationProviderId]: Integration;
    };
    return { integrations, integrationsById };
  }, [
    workspaceIntegrationSettingsResponse,
    currentPlan.type,
    user?.accessPermissions,
  ]);

  /** Retrieves the integration by provider id. Prioritizes whichever scope is connected,
   * with preference for the user-level scope */
  const getIntegration = ({
    providerId,
    scope,
  }: {
    providerId: IntegrationProviderId;
    scope?: IntegrationScope;
  }) => {
    const userIntegration = find({ providerId }, userIntegrations)!;
    const workspaceIntegration = find({ providerId }, workspaceIntegrations);

    if (scope === "user") return userIntegration;
    if (scope === "workspace") return workspaceIntegration;

    return userIntegration.isConnected
      ? userIntegration
      : workspaceIntegration?.isConnected
        ? workspaceIntegration
        : userIntegration;
  };

  const isProviderConnected = (opts: {
    providerId: IntegrationProviderId;
    scope?: IntegrationScope;
  }) => {
    return getIntegration(opts)?.isConnected ?? false;
  };

  const isSettingEnabled = (opts: {
    providerId: IntegrationProviderId;
    scope?: IntegrationScope;
    integrationKind: IntegrationSettingsKind;
  }) => {
    const integrationSettings = getIntegration({
      providerId: opts.providerId,
      scope: opts.scope,
    })?.integrationSettings;

    return integrationSettings?.byId[opts.integrationKind]?.enabled ?? false;
  };

  const getManagementUrls = (opts: {
    providerId: IntegrationProviderId;
    scope?: IntegrationScope;
  }) => {
    const defaultIntegrationSetting = getDefaultIntegrationSetting(
      getIntegration(opts)?.integrationSettings.all
    );

    return (
      defaultIntegrationSetting?.managementUrls ??
      INTEGRATION_PROVIDERS_BY_ID[opts.providerId]
    );
  };

  const getConnectUrl: GetUrlFn = (opts) => getManagementUrls(opts).connectUrl;
  const getDisconnectUrl: GetUrlFn = (opts) =>
    getManagementUrls(opts).disconnectUrl;
  const getEnableUrl: GetUrlFn = (opts) =>
    getManagementUrls(opts).enableUrl ?? null;

  const connectedCalendarIntegrations = filter(
    { category: "calendar", isConnected: true },
    userIntegrations
  );

  const connectedHighValueCalendarIntegrations = filter(
    (i) =>
      CALENDAR_PROVIDERS_BY_ID[i.providerId as CalendarProviderId].isHighValue,
    connectedCalendarIntegrations
  );

  return {
    connectedCalendarIntegrations,
    connectedHighValueCalendarIntegrations,
    hasConnectedCalendars: connectedCalendarIntegrations.length > 0,
    hasConnectedHighValueCalendars:
      connectedHighValueCalendarIntegrations.length > 0,
    isLoading,
    userIntegrations,
    userIntegrationsByCategory,
    workspaceIntegrations,
    workspaceIntegrationsById,

    getConnectUrl,
    getDisconnectUrl,
    getEnableUrl,
    getIntegration,
    isProviderConnected,
    isSettingEnabled,
  };
};

export default useIntegrations;
